import { AccountMenu, type AccountMenuPreference, densities, useDensity, useTheme } from '@ccp/ui';
import { useTranslation } from 'react-i18next';
import { useCommandActions } from '../../features/command/registry.js';
import { applyDocumentLanguage, type Language, languages } from '../../i18n/index.js';

export interface WorkspaceAccountMenuProps {
  /** Who is signed in; nothing on the sign-in pages, where the menu holds only preferences. */
  readonly userName?: string | undefined;
  readonly email?: string | undefined;
  readonly onSignOut?: (() => void) | undefined;
  /**
   * Whether density is offered.
   *
   * Off before anybody has signed in: the sign-in page is one form, and a control for how tightly
   * to pack a board nobody is looking at is noise in front of the one thing that page is for.
   */
  readonly showDensity?: boolean;
}

/**
 * The workspace's account menu: language, theme and density, then sign-out.
 *
 * It replaced three full-width selects in the header (M5.5-01). What those did beyond choosing is
 * kept here: each change is announced to a screen reader, because a theme or density switch is a
 * visual change a person who cannot see it would otherwise not know they had made, and density
 * stays reachable from the command palette.
 */
export function WorkspaceAccountMenu({
  userName,
  email,
  onSignOut,
  showDensity = false,
}: WorkspaceAccountMenuProps) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { density, setDensity } = useDensity();

  const preferences: AccountMenuPreference[] = [
    {
      id: 'theme',
      label: t('theme.label'),
      value: theme.preference,
      options: (['system', 'light', 'dark'] as const).map((value) => ({
        value,
        label: t(`theme.${value}`),
      })),
      onChange: (value) => theme.setPreference(value as typeof theme.preference),
    },
    ...(showDensity
      ? [
          {
            id: 'density',
            label: t('density.label'),
            value: density,
            options: densities.map((value) => ({ value, label: t(`density.${value}`) })),
            onChange: (value: string) => setDensity(value as typeof density),
          },
        ]
      : []),
    {
      id: 'language',
      label: t('language.label'),
      value: i18n.resolvedLanguage ?? 'en',
      // Every language is written in itself, so none of these names is translated.
      options: languages.map((value) => ({ value, label: t(`language.${value}`) })),
      onChange: (value) => {
        void i18n.changeLanguage(value as Language);
        applyDocumentLanguage(value);
      },
    },
  ];

  return (
    <>
      <AccountMenu
        name={userName}
        detail={email}
        label={userName === undefined ? t('account.preferences') : t('account.label')}
        preferences={preferences}
        signOutLabel={t('nav.signOut')}
        onSignOut={onSignOut}
      />
      {showDensity ? <DensityCommand /> : null}
      <span aria-live="polite" className="sr-only">
        {t('theme.changed', { theme: t(`theme.${theme.preference}`) })}
      </span>
      {showDensity ? (
        <span aria-live="polite" className="sr-only">
          {t('density.changed', { density: t(`density.${density}`) })}
        </span>
      ) : null}
    </>
  );
}

/** Density from the palette too: the second route to it, for the people who live in the keyboard. */
function DensityCommand() {
  const { t } = useTranslation();
  const { density, toggle } = useDensity();
  useCommandActions([
    {
      id: 'density.toggle',
      group: 'me',
      label: t('density.toggleTo', {
        density: t(`density.${density === 'compact' ? 'comfortable' : 'compact'}`),
      }),
      run: toggle,
    },
  ]);
  return null;
}
